import { forwardRef, useState } from 'react'

import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'

import { AppIcon } from '../app-icon'
import { AllIcons } from '../app-icon/types'

import { BlockSelect } from './components/select-block'
import { getSelectCurrentValue } from './lib/get-select-current-value'
import s from './styles.module.scss'

export interface Option {
  value: string
  name: string
}

export interface Props {
  error?: FieldError | boolean
  placeholder?: string
  value?: string
  title?: string
  options?: Option[]
  buttonText?: string
  disabled?: boolean
  onChange?: (data: Option) => void
  variant?: 'default' | 'secondary'
}

export const Select = forwardRef<HTMLDivElement, Props>(
  (
    {
      placeholder,
      options,
      value,
      title,
      error,
      disabled = false,
      onChange,
      variant = 'default',
    },
    ref,
  ) => {
    const { openDialog, closeDialog } = useDialogBottomSheet()

    const { t } = useTranslation()

    const [currentValue, setCurrentValue] = useState<Option | undefined>()
    const selectedValue = getSelectCurrentValue({
      currentValue,
      options,
      value,
    })

    const onSelectOption = (option: Option) => {
      closeDialog()

      if (value === undefined) {
        setCurrentValue(option)
      }

      onChange?.(option)
    }

    const onShowDialogBottomSheet = () => {
      if (disabled) return

      openDialog({
        child: (
          <div className={s.dialog}>
            {title ? <p className={s.title}>{title}</p> : null}

            <BlockSelect
              value={selectedValue}
              listOption={options}
              onChange={onSelectOption}
            />
          </div>
        ),
      })
    }

    return (
      <div
        className={clsx(s[variant], error && s.error)}
        ref={ref}
        tabIndex={0}
        onClick={onShowDialogBottomSheet}
      >
        <div className={s.content}>
          {selectedValue ? (
            <span className={s.value}>{t(selectedValue?.name)}</span>
          ) : (
            <span className={s.placeholder}>{placeholder}</span>
          )}
        </div>

        <AppIcon
          id={AllIcons.ARROW_BOTTOM}
          className={s.arrow}
          height={24}
          width={24}
        />
      </div>
    )
  },
)

Select.displayName = 'Select'
